/* ================================================================
   AboutPage.tsx — À propos de My Helper MINESEC
   Contenu seul — header/footer fournis par la route layout (App.tsx)
   ================================================================ */

import { useMemo } from 'react';
import {
  GraduationCap, Target, Eye, HeartHandshake, Users, ShieldCheck,
  BarChart3, Globe2, CheckCircle2, Sparkles, Rocket, Handshake,
  CalendarCheck, LineChart, School, ArrowRight,
} from 'lucide-react';
import { useLang } from '../../hooks/useLang';

export default function AboutPage() {
  const { lang } = useLang();
  const isFr = lang === 'fr';

  const t = useMemo(() => ({
    heroBadge: isFr ? 'À propos de nous' : 'About us',
    heroTitle: isFr ? 'My Helper MINESEC' : 'My Helper MINESEC',
    heroSubtitle: isFr
      ? "La plateforme de gestion scolaire pensée pour les établissements d'enseignement secondaire au Cameroun. Simple, fiable et conforme aux exigences du MINESEC."
      : 'The school management platform designed for secondary schools in Cameroon. Simple, reliable and compliant with MINESEC requirements.',
    stats: [
      { value: '12+', label: isFr ? 'Modules intégrés' : 'Integrated modules' },
      { value: '50+', label: isFr ? 'Établissements' : 'Schools' },
      { value: '2', label: isFr ? 'Langues (FR / EN)' : 'Languages (FR / EN)' },
      { value: '99.9%', label: isFr ? 'Disponibilité' : 'Uptime' },
    ],
    valuesTitle: isFr ? 'Ce qui nous anime' : 'What drives us',
    values: [
      {
        icon: Target,
        title: isFr ? 'Notre mission' : 'Our mission',
        desc: isFr
          ? "Digitaliser la gestion des établissements d'enseignement secondaire pour permettre aux équipes pédagogiques et administratives de se concentrer sur l'essentiel : la réussite des élèves."
          : 'Digitalize secondary school management so pedagogical and administrative teams can focus on what matters most: student success.',
      },
      {
        icon: Eye,
        title: isFr ? 'Notre vision' : 'Our vision',
        desc: isFr
          ? "Devenir la plateforme de référence de la gestion scolaire en Afrique francophone, avec un outil simple, fiable et adapté au terrain."
          : 'Become the reference school management platform in French-speaking Africa, with a simple, reliable tool adapted to the field.',
      },
      {
        icon: HeartHandshake,
        title: isFr ? 'Notre engagement' : 'Our commitment',
        desc: isFr
          ? "Accompagner chaque établissement pas à pas : formation des équipes, support réactif et évolutions continues selon vos retours."
          : 'Support every school step by step: team training, responsive support and continuous improvements based on your feedback.',
      },
    ],
    howTitle: isFr ? 'Comment ça marche ?' : 'How does it work?',
    howSubtitle: isFr
      ? 'Trois étapes simples pour transformer la gestion de votre établissement'
      : 'Three simple steps to transform your school management',
    steps: [
      {
        icon: CalendarCheck,
        step: '01',
        title: isFr ? 'Demandez une démo' : 'Request a demo',
        desc: isFr
          ? "Remplissez le formulaire — notre équipe vous contacte sous 24h pour une présentation personnalisée."
          : 'Fill in the form — our team contacts you within 24h for a personalized presentation.',
      },
      {
        icon: Rocket,
        step: '02',
        title: isFr ? 'Installation & configuration' : 'Setup & configuration',
        desc: isFr
          ? "Nous configurons la plateforme selon votre établissement : classes, matières, utilisateurs et année scolaire."
          : 'We configure the platform for your school: classes, subjects, users and academic year.',
      },
      {
        icon: LineChart,
        step: '03',
        title: isFr ? 'Formation & lancement' : 'Training & launch',
        desc: isFr
          ? "Formation de vos équipes administratives et enseignantes, puis lancement en toute sérénité avec un support continu."
          : 'Training for your administrative and teaching staff, then a smooth launch with continuous support.',
      },
    ],
    featuresTitle: isFr ? 'Ce que la plateforme vous apporte' : 'What the platform brings you',
    featuresSubtitle: isFr
      ? "Fini les cahiers et les fichiers éparpillés — toutes vos données centralisées dans un espace unique et sécurisé."
      : 'No more notebooks and scattered files — all your data centralized in a single secure space.',
    features: [
      { icon: Users, label: isFr ? 'Élèves & Enseignants' : 'Students & Teachers' },
      { icon: BarChart3, label: isFr ? 'Notes & Bulletins' : 'Grades & Reports' },
      { icon: School, label: isFr ? 'Classes & Emplois du temps' : 'Classes & Timetables' },
      { icon: ShieldCheck, label: isFr ? 'Sécurité & Rôles fins' : 'Security & Fine roles' },
      { icon: Globe2, label: isFr ? 'Accès web partout' : 'Web access anywhere' },
      { icon: Handshake, label: isFr ? 'Support & Formation' : 'Support & Training' },
    ],
    checklist: isFr
      ? [
          'Gestion complète des élèves, enseignants et classes',
          'Bulletins et relevés de notes générés automatiquement',
          'Budget et comptabilité avec équilibrage automatique',
          'Emplois du temps et planification sans conflit',
          "Suivi disciplinaire et statistiques en temps réel",
          "Site public de l'établissement avec actualités",
        ]
      : [
          'Complete management of students, teachers and classes',
          'Automatic report cards and transcripts',
          'Budget and accounting with automatic balancing',
          'Timetables and conflict-free planning',
          'Discipline tracking and real-time statistics',
          'School public website with news',
        ],
    ctaTitle: isFr ? 'Prêt à moderniser votre établissement ?' : 'Ready to modernize your school?',
    ctaSubtitle: isFr
      ? "Demandez une démonstration gratuite et découvrez comment la plateforme s'adapte à votre école."
      : 'Request a free demo and discover how the platform adapts to your school.',
    ctaBtn: isFr ? 'Demander une démo' : 'Request a demo',
  }), [isFr]);

  return (
    <>
      {/* ═══════════ HERO ═══════════ */}
      <section className="relative bg-school-blue pt-32 lg:pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-school-gold/10 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-school-gold" />
            <span className="text-school-gold text-sm font-semibold">{t.heroBadge}</span>
          </span>
          <h1 className="text-4xl lg:text-6xl font-black text-white mb-5 leading-tight">
            {t.heroTitle}
          </h1>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto leading-relaxed">
            {t.heroSubtitle}
          </p>
        </div>
      </section>

      {/* ═══════════ STATS ═══════════ */}
      <section className="bg-school-blue pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 bg-white/10 border border-white/10 rounded-2xl p-6 lg:p-8 backdrop-blur">
            {t.stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-3xl lg:text-4xl font-black text-school-gold">{s.value}</div>
                <div className="text-blue-100 text-sm mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ MISSION / VISION / ENGAGEMENT ═══════════ */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-black text-school-blue mb-3">{t.valuesTitle}</h2>
            <div className="w-16 h-1 bg-school-gold mx-auto rounded-full" />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {t.values.map((v) => (
              <div
                key={v.title}
                className="group bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:bg-school-blue hover:border-school-blue transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-xl bg-school-blue/10 group-hover:bg-school-gold flex items-center justify-center mb-5 transition-colors">
                  <v.icon className="w-6 h-6 text-school-blue group-hover:text-school-blue transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-white mb-3 transition-colors">{v.title}</h3>
                <p className="text-gray-600 group-hover:text-blue-100 leading-relaxed transition-colors">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ COMMENT ÇA MARCHE ═══════════ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-black text-school-blue mb-3">{t.howTitle}</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">{t.howSubtitle}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {t.steps.map((s) => (
              <div key={s.step} className="relative bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-xl hover:border-school-gold/40 transition-all">
                <span className="absolute top-6 right-6 text-5xl font-black text-gray-100 select-none">{s.step}</span>
                <div className="w-14 h-14 rounded-2xl bg-school-blue flex items-center justify-center mb-5 shadow-lg">
                  <s.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ FONCTIONNALITÉS ═══════════ */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-extrabold text-school-blue mb-4">{t.featuresTitle}</h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">{t.featuresSubtitle}</p>
              <ul className="space-y-3">
                {t.checklist.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-gray-700">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {t.features.map((c) => (
                <div
                  key={c.label}
                  className="bg-gray-50 rounded-2xl p-6 border border-gray-100 flex flex-col items-center text-center gap-3 hover:bg-school-blue transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl bg-school-blue/10 group-hover:bg-white/20 flex items-center justify-center transition-colors">
                    <c.icon className="w-6 h-6 text-school-blue group-hover:text-white transition-colors" />
                  </div>
                  <span className="font-semibold text-gray-800 group-hover:text-white text-sm transition-colors">{c.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ CTA ═══════════ */}
      <section className="py-16 bg-school-blue">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-extrabold text-white mb-4">{t.ctaTitle}</h2>
          <p className="text-blue-100 text-lg mb-8">{t.ctaSubtitle}</p>
          <a
            href="/#demo"
            className="inline-flex items-center gap-2 px-8 py-4 bg-school-gold text-school-blue font-bold rounded-xl hover:opacity-90 transition-all shadow-lg hover:scale-105"
          >
            {t.ctaBtn}
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
    </>
  );
}